import React, { useState, useEffect } from 'react';
import axios from 'axios';
// Import API config to set axios defaults
import '../config/api';
import {
    Container,
    Typography,
    Box,
    Grid,
    Card,
    CardMedia,
    CardContent,
    CardActions,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    useTheme,
    useMediaQuery,
    Snackbar,
    Alert,
    Paper,
    Stack
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import {
    ShoppingCart as ShoppingCartIcon,
    Add as AddIcon,
    Remove as RemoveIcon,
    WhatsApp as WhatsAppIcon,
    Close as CloseIcon,
    LocalShipping as ShippingIcon,
    Verified as VerifiedIcon,
} from '@mui/icons-material';

interface Product {
    id: string;
    nama: string;
    harga: number;
    gambar: string;
    deskripsi: string;
    inStock: boolean;
}

// Products will be loaded from Google Sheets API

interface CartItem {
    product: Product;
    quantity: number;
}

const Marketplace: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [cart, setCart] = useState<CartItem[]>([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [quantity, setQuantity] = useState(1);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [cartQuantities, setCartQuantities] = useState<{ [productId: string]: number }>({});
    const [openImagePreview, setOpenImagePreview] = useState<string | null>(null);

    // Fetch products from Google Sheets
    useEffect(() => {
        const fetchProducts = async () => {
            try {
                setLoading(true);
                const response = await axios.get('/api/bookings?action=marketplace');
                const productsData = response.data.map((item: any, index: number) => ({
                    id: `product-${index}`,
                    nama: item.Nama || item.nama || 'Product',
                    // Fix: handle harga supaya tidak NaN
                    harga: (isNaN(Number(item.Harga ?? item.harga)) || (!item.Harga && !item.harga))
                        ? 0
                        : Number(item.Harga ?? item.harga),
                    gambar: item.Gambar || item.gambar || '/images/logo_booking_mabar.png',
                    deskripsi: item.Deskripsi || item.deskripsi || 'No description available',
                    inStock: true
                }));
                setProducts(productsData);
            } catch (error) {
                console.error('Error fetching marketplace products:', error);
                setError('Gagal memuat data produk marketplace');
            } finally {
                setLoading(false);
            }
        };

        fetchProducts();
    }, []);

    const addToCart = (product: Product, qty: number = 1) => {
        setCart(prevCart => {
            const existingItem = prevCart.find(item => item.product.id === product.id);
            if (existingItem) {
                return prevCart.map(item =>
                    item.product.id === product.id
                        ? { ...item, quantity: item.quantity + qty }
                        : item
                );
            }
            return [...prevCart, { product, quantity: qty }];
        });
        setSnackbarMessage(`${product.nama} berhasil ditambahkan ke keranjang!`);
        setSnackbarOpen(true);
        setCartQuantities(prev => ({ ...prev, [product.id]: 1 })); // reset qty
    };

    const removeFromCart = (productId: string) => {
        setCart(prevCart => prevCart.filter(item => item.product.id !== productId));
    };

    const updateQuantity = (productId: string, newQuantity: number) => {
        if (newQuantity <= 0) {
            removeFromCart(productId);
            return;
        }
        setCart(prevCart =>
            prevCart.map(item =>
                item.product.id === productId
                    ? { ...item, quantity: newQuantity }
                    : item
            )
        );
    };

    const getTotalPrice = () => {
        return cart.reduce((total, item) => total + (item.product.harga * item.quantity), 0);
    };

    const getTotalItems = () => {
        return cart.reduce((total, item) => total + item.quantity, 0);
    };

    const handleBuyNow = () => {
        if (cart.length === 0) {
            setSnackbarMessage('Keranjang kosong! Silakan pilih produk terlebih dahulu.');
            setSnackbarOpen(true);
            return;
        }

        const message = generateWhatsAppMessage();
        const phoneNumber = "6283890485939";
        const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
        
        window.open(whatsappUrl, '_blank');
    };

    const generateWhatsAppMessage = () => {
        let message = "🛍️ *PESANAN MERCHANDISE RAKETERS*\n\n";
        message += "Halo! Saya ingin memesan merchandise berikut:\n\n";
        
        cart.forEach((item, index) => {
            message += `${index + 1}. *${item.product.nama}*\n`;
            message += `   💰 Harga: Rp ${item.product.harga.toLocaleString('id-ID')}\n`;
            message += `   📦 Kuantitas: ${item.quantity}\n`;
            message += `   💵 Subtotal: Rp ${(item.product.harga * item.quantity).toLocaleString('id-ID')}\n\n`;
        });
        
        message += `💰 *TOTAL PEMBELIAN: Rp ${getTotalPrice().toLocaleString('id-ID')}*\n\n`;
        message += "Terima kasih! 🙏";
        
        return message;
    };

    const formatPrice = (price: number) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0
        }).format(price);
    };

    return (
        <>
            <Helmet>
                <title>Marketplace Merchandise RaketerS - Merchandise Badminton Bandung</title>
                <meta name="description" content="Toko online merchandise RaketerS. Beli merchandise badminton seperti jersey, raket, dan aksesoris badminton berkualitas untuk komunitas mabar badminton Bandung." />
                <meta name="robots" content="index, follow" />
                <link rel="canonical" href="https://raketers.com/marketplace" />
            </Helmet>
            <Container maxWidth="lg" sx={{ py: 4 }}>
                {/* Header */}
                <Box sx={{ textAlign: 'center', mb: 6 }}>
                    <Typography 
                        variant="h3" 
                        component="h1" 
                        gutterBottom
                        sx={{
                            fontWeight: 'bold',
                            background: theme.palette.mode === 'light'
                                ? 'linear-gradient(45deg, #1976d2, #42a5f5, #1976d2)'
                                : 'linear-gradient(45deg, #64b5f6, #90caf9, #64b5f6)',
                            backgroundSize: '200% 200%',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            animation: 'shimmer 3s ease-in-out infinite',
                            '@keyframes shimmer': {
                                '0%': { backgroundPosition: '0% 50%' },
                                '50%': { backgroundPosition: '100% 50%' },
                                '100%': { backgroundPosition: '0% 50%' },
                            },
                            fontSize: isMobile ? '2rem' : '3rem'
                        }}
                    >
                        🛍️ Marketplace RaketerS
                    </Typography>
                <Typography 
                    variant="h6" 
                    color="text.secondary"
                    sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}
                >
                    Temukan merchandise eksklusif RaketerS dengan kualitas terbaik dan desain yang menawan
                </Typography>

                {/* Cart Summary */}
                {cart.length > 0 && (
                    <Paper
                        elevation={3}
                        sx={{
                            p: 3,
                            mb: 4,
                            background: theme.palette.mode === 'light'
                                ? 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)'
                                : 'linear-gradient(135deg, rgba(13,71,161,0.1) 0%, rgba(21,101,192,0.05) 100%)',
                            backdropFilter: 'blur(10px)',
                            border: theme.palette.mode === 'light'
                                ? '1px solid rgba(255,255,255,0.3)'
                                : '1px solid rgba(255,255,255,0.1)',
                            borderRadius: 3
                        }}
                    >
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                    🛒 Keranjang Belanja
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {getTotalItems()} item • Total: {formatPrice(getTotalPrice())}
                                </Typography>
                            </Box>
                            <Button
                                variant="contained"
                                startIcon={<WhatsAppIcon />}
                                onClick={handleBuyNow}
                                sx={{
                                    background: 'linear-gradient(45deg, #25D366, #128C7E)',
                                    px: 3,
                                    py: 1.5,
                                    borderRadius: 2,
                                    fontWeight: 'bold',
                                    boxShadow: '0 4px 12px rgba(37, 211, 102, 0.3)',
                                    '&:hover': {
                                        background: 'linear-gradient(45deg, #128C7E, #25D366)',
                                        boxShadow: '0 6px 16px rgba(37, 211, 102, 0.4)',
                                        transform: 'translateY(-2px)'
                                    }
                                }}
                            >
                                Beli Sekarang
                            </Button>
                        </Stack>

                        {/* Cart Items */}
                        <Box sx={{ mt: 2 }}>
                            {cart.map((item) => (
                                <Box
                                    key={item.product.id}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        py: 1,
                                        px: 2,
                                        mb: 1,
                                        background: theme.palette.mode === 'light'
                                            ? 'rgba(255,255,255,0.7)'
                                            : 'rgba(255,255,255,0.05)',
                                        borderRadius: 2
                                    }}
                                >
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {item.product.nama} × {item.quantity}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                            {formatPrice(item.product.harga * item.quantity)}
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                                            sx={{ color: theme.palette.error.main }}
                                        >
                                            <RemoveIcon fontSize="small" />
                                        </IconButton>
                                        <Typography variant="body2" sx={{ minWidth: 20, textAlign: 'center' }}>
                                            {item.quantity}
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                                            sx={{ color: theme.palette.success.main }}
                                        >
                                            <AddIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Paper>
                )}
            </Box>

            {/* Products Grid */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                    <Typography variant="h6">Memuat produk marketplace...</Typography>
                </Box>
            ) : error ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                    <Alert severity="error">{error}</Alert>
                </Box>
            ) : (
                <Grid container spacing={3}>
                    {products.map((product) => (
                    <Grid item xs={12} sm={6} md={4} key={product.id}>
                        <Card
                            sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-8px)',
                                    boxShadow: theme.palette.mode === 'light'
                                        ? '0 12px 40px rgba(25,118,210,0.15)'
                                        : '0 12px 40px rgba(13,71,161,0.3)',
                                },
                                borderRadius: 3,
                                background: theme.palette.mode === 'light'
                                    ? 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
                                    : (isMobile ? '#0a1626' : 'linear-gradient(135deg, rgba(13,71,161,0.08) 0%, rgba(21,101,192,0.04) 100%)'),
                                backdropFilter: 'blur(10px)',
                                border: theme.palette.mode === 'light'
                                    ? '1px solid rgba(255,255,255,0.2)'
                                    : '1px solid rgba(99,170,255,0.22)',
                            }}
                        >
                            <Box
                                sx={{
                                    position: 'relative',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    setSelectedProduct(product);
                                    setOpenDialog(true);
                                    setQuantity(1);
                                }}
                            >
                                <CardMedia
                                    component="img"
                                    height="200"
                                    image={product.gambar}
                                    alt={product.nama}
                                    sx={{
                                        objectFit: 'contain',
                                        p: 2,
                                        background: theme.palette.mode === 'light'
                                            ? 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)'
                                            : 'linear-gradient(135deg, rgba(13,71,161,0.1) 0%, rgba(21,101,192,0.05) 100%)',
                                    }}
                                />
                                {!product.inStock && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 8,
                                            background: 'rgba(0,0,0,0.7)',
                                            color: 'white',
                                            px: 1,
                                            py: 0.5,
                                            borderRadius: 1,
                                            fontSize: '0.75rem',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        Stok Habis
                                    </Box>
                                )}
                            </Box>
                            <CardContent sx={{ flexGrow: 1 }}>
                                <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    {product.nama}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 60 }}>
                                    {product.deskripsi}
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                    {formatPrice(product.harga)}
                                </Typography>
                            </CardContent>
                            <CardActions sx={{ p: 2, pt: 0, flexDirection: 'column', alignItems: 'stretch' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <IconButton onClick={() => setCartQuantities(q => ({ ...q, [product.id]: Math.max(1, (q[product.id]||1) - 1) }))}><RemoveIcon /></IconButton>
                                    <TextField value={cartQuantities[product.id] || 1} size="small" inputProps={{ min: 1, style: { textAlign: 'center', width: 48 } }}
                                        onChange={e => setCartQuantities(q => ({ ...q, [product.id]: Math.max(1, parseInt(e.target.value)||1) }))} />
                                    <IconButton onClick={() => setCartQuantities(q => ({ ...q, [product.id]: (q[product.id]||1) + 1 }))}><AddIcon /></IconButton>
                                </Box>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    startIcon={<ShoppingCartIcon />}
                                    onClick={() => addToCart(product, cartQuantities[product.id] || 1)}
                                    disabled={!product.inStock}
                                    sx={{ borderRadius: 2, fontWeight: 'bold' }}
                                >
                                    Tambah ke Keranjang
                                </Button>
                            </CardActions>
                        </Card>
                    </Grid>
                ))}
                </Grid>
            )}

            {/* Features Section */}
            <Box sx={{ mt: 8, textAlign: 'center' }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 4 }}>
                    Mengapa Belanja di Marketplace RaketerS?
                </Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <Paper
                            elevation={2}
                            sx={{
                                p: 3,
                                height: '100%',
                                textAlign: 'center',
                                background: theme.palette.mode === 'light'
                                    ? 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)'
                                    : 'linear-gradient(135deg, rgba(76,175,80,0.1) 0%, rgba(56,142,60,0.05) 100%)',
                                borderRadius: 3
                            }}
                        >
                            <VerifiedIcon sx={{ fontSize: 48, color: theme.palette.success.main, mb: 2 }} />
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                Produk Asli
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Semua merchandise dijamin 100% asli dan berkualitas tinggi
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Paper
                            elevation={2}
                            sx={{
                                p: 3,
                                height: '100%',
                                textAlign: 'center',
                                background: theme.palette.mode === 'light'
                                    ? 'linear-gradient(135deg, #fff3e0 0%, #ffcc02 100%)'
                                    : 'linear-gradient(135deg, rgba(255,152,0,0.1) 0%, rgba(255,193,7,0.05) 100%)',
                                borderRadius: 3
                            }}
                        >
                            <ShippingIcon sx={{ fontSize: 48, color: theme.palette.warning.main, mb: 2 }} />
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                Pengiriman Cepat
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Pesanan akan dikirim segera setelah konfirmasi pembayaran
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Paper
                            elevation={2}
                            sx={{
                                p: 3,
                                height: '100%',
                                textAlign: 'center',
                                background: theme.palette.mode === 'light'
                                    ? 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)'
                                    : 'linear-gradient(135deg, rgba(25,118,210,0.1) 0%, rgba(33,150,243,0.05) 100%)',
                                borderRadius: 3
                            }}
                        >
                            <WhatsAppIcon sx={{ fontSize: 48, color: '#25D366', mb: 2 }} />
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                Customer Service
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Layanan pelanggan 24/7 melalui WhatsApp untuk bantuan terbaik
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>

            {/* Product Detail Dialog */}
            <Dialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        background: theme.palette.mode === 'light'
                            ? 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
                            : 'linear-gradient(135deg, rgba(18,18,18,0.95) 0%, rgba(30,30,30,0.95) 100%)',
                        backdropFilter: 'blur(10px)',
                        border: theme.palette.mode === 'light'
                            ? '1px solid rgba(255,255,255,0.2)'
                            : '1px solid rgba(255,255,255,0.1)',
                    }
                }}
            >
                {selectedProduct && (
                    <>
                        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                {selectedProduct.nama}
                            </Typography>
                            <IconButton onClick={() => setOpenDialog(false)}>
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent>
                            <Box sx={{ textAlign: 'center', mb: 3 }}>
                                <Box
                                    component="img"
                                    src={selectedProduct.gambar}
                                    alt={selectedProduct.nama}
                                    sx={{
                                        width: '100%',
                                        maxWidth: 300,
                                        height: 200,
                                        objectFit: 'contain',
                                        borderRadius: 2,
                                        background: theme.palette.mode === 'light'
                                            ? 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)'
                                            : 'linear-gradient(135deg, rgba(13,71,161,0.1) 0%, rgba(21,101,192,0.05) 100%)',
                                        p: 2
                                    }}
                                />
                            </Box>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                {selectedProduct.deskripsi}
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 3 }}>
                                {formatPrice(selectedProduct.harga)}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                    Kuantitas:
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <IconButton
                                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                        sx={{ border: '1px solid', borderColor: 'divider' }}
                                    >
                                        <RemoveIcon />
                                    </IconButton>
                                    <TextField
                                        value={quantity}
                                        onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                                        inputProps={{ min: 1, style: { textAlign: 'center', width: 60 } }}
                                        variant="outlined"
                                        size="small"
                                    />
                                    <IconButton
                                        onClick={() => setQuantity(quantity + 1)}
                                        sx={{ border: '1px solid', borderColor: 'divider' }}
                                    >
                                        <AddIcon />
                                    </IconButton>
                                </Box>
                            </Box>
                        </DialogContent>
                        <DialogActions sx={{ p: 3, pt: 0 }}>
                            <Button
                                onClick={() => setOpenDialog(false)}
                                variant="outlined"
                                sx={{ borderRadius: 2 }}
                            >
                                Batal
                            </Button>
                            <Button
                                onClick={() => {
                                    addToCart(selectedProduct, quantity);
                                    setOpenDialog(false);
                                }}
                                variant="contained"
                                startIcon={<ShoppingCartIcon />}
                                disabled={!selectedProduct.inStock}
                                sx={{
                                    borderRadius: 2,
                                    fontWeight: 'bold',
                                    px: 3,
                                    background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
                                    '&:hover': {
                                        background: 'linear-gradient(45deg, #1565c0, #1976d2)',
                                    }
                                }}
                            >
                                Tambah ke Keranjang
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity="success"
                    sx={{ borderRadius: 2 }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>

            {/* Image Preview Dialog */}
            <Dialog open={!!openImagePreview} onClose={() => setOpenImagePreview(null)} maxWidth="sm">
                <IconButton onClick={() => setOpenImagePreview(null)} sx={{ position: 'absolute', top: 8, right: 8, zIndex: 99 }}><CloseIcon /></IconButton>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2, minHeight: 400 }}>
                    {openImagePreview && (<img src={openImagePreview} alt='Preview' style={{ maxWidth: '100%', maxHeight: 500, borderRadius: 12 }} />)}
                </Box>
            </Dialog>
        </Container>
        </>
    );
};

export default Marketplace;
